import React, { useState } from 'react';
import { 
  ArrowLeftIcon,
  UserMinusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiPost } from '../utils/api';
import { getUsersData } from '../data/users';

// Empty x-type bucket used to reset cascade-detection state
const EMPTY_X_USERS = { agents: [], superAgents: [], cities: [], countries: [] };

// Order in which x-types must be detached (bottom-up). Each entry maps
// the bucket key in `xUsers` to a human-readable label for UI/progress.
const X_TYPE_ORDER = [
  { key: 'agents', label: 'x-agents' },
  { key: 'superAgents', label: 'x super-agents' },
  { key: 'cities', label: 'x cities' },
  { key: 'countries', label: 'x countries' }
];

const DetachInactivePlayers = () => {
  const [inputText, setInputText] = useState('');
  const [usernames, setUsernames] = useState([]);
  const [xUsers, setXUsers] = useState(EMPTY_X_USERS);
  const [expandedXSections, setExpandedXSections] = useState(new Set());
  const [isValidating, setIsValidating] = useState(false);
  const [isDeepValidating, setIsDeepValidating] = useState(false);
  const [isDetaching, setIsDetaching] = useState(false);
  const [isAutoDetaching, setIsAutoDetaching] = useState(false);
  const [autoDetachProgress, setAutoDetachProgress] = useState(null);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());
  const [detachingPlayers, setDetachingPlayers] = useState(new Set());
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const navigate = useNavigate();

  const toggleXSection = (key) => {
    setExpandedXSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const totalXUsers = xUsers.agents.length + xUsers.superAgents.length + xUsers.cities.length + xUsers.countries.length;

  const togglePlayerExpanded = (username) => {
    const newExpanded = new Set(expandedPlayers);
    if (newExpanded.has(username)) {
      newExpanded.delete(username);
    } else {
      newExpanded.add(username);
    }
    setExpandedPlayers(newExpanded);
  };

  const handleAddUsernames = () => {
    if (!inputText.trim()) {
      return;
    }

    // Parse comma-separated and newline-separated usernames
    const rawUsernames = inputText.split(/[,\n\r]+/);
    
    // Clean and trim each username
    const cleanedUsernames = rawUsernames
      .map(username => username.trim())
      .filter(username => username.length > 0)
      .filter((username, index, self) => self.indexOf(username) === index); // Remove duplicates

    if (cleanedUsernames.length === 0) {
      setError('No valid usernames found. Please enter at least one username.');
      return;
    }

    setUsernames(cleanedUsernames);
    setInputText('');
    setError(null);
    setValidationResults(null);
    setSuccessMessage(null);
  };

  const handleAutoLoad = async () => {
    setIsAutoLoading(true);
    setError(null);
    setValidationResults(null);
    setSuccessMessage(null);

    try {
      console.log('Auto-loading inactive players from cached user tree...');

      // Define thresholds for each currency
      const THRESHOLDS = {
        'USD': 10,
        'LBP': 1000000,
        'TND': 10,
        'TRY': 15,
        'IQD': 15000,
        'KWD': 3
      };

      const INACTIVITY_DAYS = 45;
      const inactivityThreshold = Date.now() - (INACTIVITY_DAYS * 24 * 60 * 60 * 1000);

      // Get cached user tree data
      const { success, data } = await getUsersData();
      
      if (!success || !data || !data.users) {
        throw new Error('Failed to load user tree data');
      }

      const inactivePlayers = [];

      // Filter players from the cached user tree
      for (const [username, user] of Object.entries(data.users)) {
        try {
          // Must be a player type
          if (user.type !== 'player') {
            continue;
          }

          // Check UserAttributes for last login and creation date
          const userAttributes = user.attributes || {};
          
          // Check lastLoginTime (only if it exists)
          if (userAttributes.lastLoginTime) {
            const lastLoginDate = new Date(userAttributes.lastLoginTime);
            if (!isNaN(lastLoginDate.getTime()) && lastLoginDate.getTime() > inactivityThreshold) {
              // Player logged in within the last 45 days - skip
              continue;
            }
          }
          // If lastLoginTime is missing, skip login time check and rely on balance thresholds

          // Check creationDate (could be in attributes or directly on user)
          const creationDate = userAttributes.creationDate || user.creationDate;
          if (creationDate) {
            const creationDateObj = new Date(creationDate);
            if (!isNaN(creationDateObj.getTime()) && creationDateObj.getTime() > inactivityThreshold) {
              // Player account created within the last 45 days - skip
              continue;
            }
          }

          // Check wallet balances
          const balances = user.balances || {};
          
          if (Object.keys(balances).length === 0) {
            // No wallet data - include this player
            inactivePlayers.push(username);
            continue;
          }

          // Check if ALL relevant currency balances are below threshold
          let allBalancesBelowThreshold = true;
          let hasCheckedCurrency = false;

          for (const [currency, balanceData] of Object.entries(balances)) {
            // Only check currencies we have thresholds for
            if (THRESHOLDS[currency] !== undefined) {
              hasCheckedCurrency = true;
              // Extract numeric balance (handle both object and number formats)
              const balance = typeof balanceData === 'object' 
                ? (parseFloat(balanceData.amount) || parseFloat(balanceData.value) || 0)
                : parseFloat(balanceData) || 0;

              if (balance >= THRESHOLDS[currency]) {
                // This currency is above threshold - player doesn't qualify
                allBalancesBelowThreshold = false;
                break;
              }
            }
            // If currency not in threshold list, ignore it
          }

          // Player qualifies if:
          // 1. We checked at least one currency AND all checked currencies are below threshold
          // 2. OR we have balances but none match our threshold list (consider inactive)
          if (allBalancesBelowThreshold) {
            inactivePlayers.push(username);
          }

        } catch (playerError) {
          console.warn(`Error checking player ${username}:`, playerError.message);
          // Continue with next player
        }
      }

      console.log(`Found ${inactivePlayers.length} inactive players matching criteria`);

      // ---- Bottom-up cascade: detect xagents / x super-agents / x cities / x countries ----
      //
      // Definition (recursive):
      //   - A player is "x" if it is in the inactivePlayers set.
      //   - Any other type is "x" if it has no children OR every child is "x".
      //
      // We walk the cached tree from the root, post-order, so children are
      // resolved before their parent. The agent/super-agent/city/country
      // detection only fires when the entire subtree is inactive.
      const inactivePlayerSet = new Set(inactivePlayers);
      const xAgents = [];
      const xSuperAgents = [];
      const xCities = [];
      const xCountries = [];

      const computingCache = new Map(); // username -> boolean (is x?)
      const inProgress = new Set();

      const isX = (username, depth = 0) => {
        if (depth > 100) return false; // safety against malformed trees
        if (computingCache.has(username)) return computingCache.get(username);
        if (inProgress.has(username)) return false; // cycle guard
        const user = data.users[username];
        if (!user) return false;

        inProgress.add(username);

        let result;
        if (user.type === 'player') {
          result = inactivePlayerSet.has(username);
        } else {
          const childUsernames = Array.isArray(user.children) ? user.children : [];
          // Empty children = trivially x (still subject to type-specific bucketing below)
          if (childUsernames.length === 0) {
            result = true;
          } else {
            // Recurse into ALL children (even if one is non-x, we still want
            // to populate buckets for deeper x-subtrees).
            let allX = true;
            for (const childUsername of childUsernames) {
              const child = data.users[childUsername];
              if (!child) {
                // Missing child reference -> can't confirm subtree is inactive
                allX = false;
                continue;
              }
              if (!isX(childUsername, depth + 1)) {
                allX = false;
              }
            }
            result = allX;
          }
        }

        inProgress.delete(username);
        computingCache.set(username, result);

        if (result) {
          switch (user.type) {
            case 'agent':
              xAgents.push(username);
              break;
            case 'super-agent':
              xSuperAgents.push(username);
              break;
            case 'city':
              xCities.push(username);
              break;
            case 'country':
              xCountries.push(username);
              break;
            default:
              break;
          }
        }
        return result;
      };

      if (data.rootId) {
        isX(data.rootId);
      } else {
        // Fallback if rootId not set: walk every entry. Cache makes this cheap.
        Object.keys(data.users).forEach((u) => isX(u));
      }

      console.log(
        `Cascade detected: xAgents=${xAgents.length}, xSuperAgents=${xSuperAgents.length}, xCities=${xCities.length}, xCountries=${xCountries.length}`
      );

      // ---- Update state with merged results ----
      let addedPlayers = 0;
      if (inactivePlayers.length > 0) {
        const existingSet = new Set(usernames);
        const newPlayers = inactivePlayers.filter((p) => !existingSet.has(p));
        addedPlayers = newPlayers.length;
        if (newPlayers.length > 0) {
          setUsernames([...usernames, ...newPlayers]);
        }
      }

      // Replace x-user buckets entirely with the latest detection so they
      // stay consistent with the current `inactivePlayers` set.
      setXUsers({
        agents: xAgents,
        superAgents: xSuperAgents,
        cities: xCities,
        countries: xCountries
      });

      const totalX = xAgents.length + xSuperAgents.length + xCities.length + xCountries.length;
      const messageParts = [];
      if (inactivePlayers.length > 0) {
        messageParts.push(
          addedPlayers > 0
            ? `Added ${addedPlayers} inactive player(s) (${inactivePlayers.length} total found)`
            : `Found ${inactivePlayers.length} inactive player(s), all already in the list`
        );
      } else {
        messageParts.push('No inactive players found');
      }
      if (totalX > 0) {
        const breakdown = [
          xAgents.length && `${xAgents.length} x-agent(s)`,
          xSuperAgents.length && `${xSuperAgents.length} x super-agent(s)`,
          xCities.length && `${xCities.length} x city(ies)`,
          xCountries.length && `${xCountries.length} x country(ies)`
        ]
          .filter(Boolean)
          .join(', ');
        messageParts.push(`Detected ${totalX} inactive hierarchy node(s): ${breakdown}`);
      }
      setSuccessMessage(messageParts.join('. ') + '.');

    } catch (err) {
      console.error('Error auto-loading players:', err);
      setError(err.message || 'Failed to auto-load players');
    } finally {
      setIsAutoLoading(false);
    }
  };

  const handleRemoveUsername = (username) => {
    setUsernames(usernames.filter(u => u !== username));
    setValidationResults(null);
    setSuccessMessage(null);
  };

  const handleClearAll = () => {
    setUsernames([]);
    setXUsers(EMPTY_X_USERS);
    setExpandedXSections(new Set());
    setValidationResults(null);
    setError(null);
    setSuccessMessage(null);
  };

  const handleValidate = async () => {
    if (usernames.length === 0) {
      return;
    }

    const MAX_PLAYERS = 300;
    const playersToValidate = usernames.slice(0, MAX_PLAYERS);
    const isLimited = usernames.length > MAX_PLAYERS;

    if (isLimited) {
      setError(`Note: You have ${usernames.length} players, but only the first ${MAX_PLAYERS} will be validated to prevent timeouts.`);
    }

    setIsValidating(true);
    setValidationResults(null);
    if (!isLimited) {
      setError(null);
    }
    setSuccessMessage(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      console.log(`Validating ${playersToValidate.length} players...`);

      const response = await apiPost(
        'coreAdmin',
        '/coreadmin/validate-detach-players',
        { usernames: playersToValidate },
        token
      );

      console.log('Validation response:', response);

      if (response.success) {
        setValidationResults(response.validation);
        setExpandedPlayers(new Set()); // Reset expanded state
        if (response.validation.validCount === 0) {
          if (isLimited) {
            setError(`No players are valid for detachment (checked first ${MAX_PLAYERS} of ${usernames.length}). See validation results below.`);
          } else {
            setError('No players are valid for detachment. See validation results below.');
          }
        } else if (isLimited) {
          setSuccessMessage(`Validation complete: ${response.validation.validCount} player(s) are valid (checked first ${MAX_PLAYERS} of ${usernames.length} players).`);
        }
      } else {
        throw new Error(response.error || 'Failed to validate players');
      }
    } catch (err) {
      console.error('Error validating players:', err);
      setError(err.message || 'Failed to validate players');
    } finally {
      setIsValidating(false);
    }
  };

  const handleDeepValidate = async () => {
    if (usernames.length === 0) {
      return;
    }

    const MAX_PLAYERS = 300;
    const playersToValidate = usernames.slice(0, MAX_PLAYERS);
    const isLimited = usernames.length > MAX_PLAYERS;

    if (isLimited) {
      setError(`Note: You have ${usernames.length} players, but only the first ${MAX_PLAYERS} will be validated to prevent timeouts.`);
    }

    setIsDeepValidating(true);
    setValidationResults(null);
    if (!isLimited) {
      setError(null);
    }
    setSuccessMessage(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      console.log(`Deep validating ${playersToValidate.length} players (checking backend activity)...`);

      const response = await apiPost(
        'coreAdmin',
        '/coreadmin/deep-validate-players',
        { usernames: playersToValidate },
        token
      );

      console.log('Deep validation response:', response);

      if (response.success) {
        setValidationResults(response.validation);
        setExpandedPlayers(new Set()); // Reset expanded state
        if (response.validation.validCount === 0) {
          if (isLimited) {
            setError(`No players are valid for detachment (checked first ${MAX_PLAYERS} of ${usernames.length}). See validation results below.`);
          } else {
            setError('No players are valid for detachment. See validation results below.');
          }
        } else {
          const message = isLimited 
            ? `Deep validation complete: ${response.validation.validCount} player(s) are truly inactive (checked first ${MAX_PLAYERS} of ${usernames.length} players).`
            : `Deep validation complete: ${response.validation.validCount} player(s) are truly inactive.`;
          setSuccessMessage(message);
        }
      } else {
        throw new Error(response.error || 'Failed to deep validate players');
      }
    } catch (err) {
      console.error('Error deep validating players:', err);
      setError(err.message || 'Failed to deep validate players');
    } finally {
      setIsDeepValidating(false);
    }
  };

  const handleDetachSingle = async (username) => {
    if (!username) return;

    setDetachingPlayers(new Set([...detachingPlayers, username]));
    setError(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      console.log('Detaching single player:', username);

      const response = await apiPost(
        'coreAdmin',
        '/coreadmin/detach-players',
        { usernames: [username] },
        token
      );

      console.log('Detach response:', response);

      if (response.success && response.detachedCount > 0) {
        // Remove player from validation results
        setValidationResults(prev => {
          const updatedPlayers = prev.players.filter(p => p.username !== username);
          return {
            ...prev,
            players: updatedPlayers,
            validCount: prev.validCount - 1,
            totalCount: prev.totalCount - 1
          };
        });
        
        // Remove from usernames list
        setUsernames(prev => prev.filter(u => u !== username));
        
        setSuccessMessage(`Successfully detached ${username}!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.error || 'Failed to detach player');
      }
    } catch (err) {
      console.error('Error detaching player:', err);
      setError(err.message || 'Failed to detach player');
    } finally {
      setDetachingPlayers(prev => {
        const newSet = new Set(prev);
        newSet.delete(username);
        return newSet;
      });
    }
  };

  const handleDetachAll = async () => {
    if (!validationResults || validationResults.validCount === 0) {
      return;
    }

    setIsDetaching(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const validUsernames = validationResults.players
        .filter(p => p.valid)
        .map(p => p.username);

      console.log('Detaching all valid players:', validUsernames);

      const response = await apiPost(
        'coreAdmin',
        '/coreadmin/detach-players',
        { usernames: validUsernames },
        token
      );

      console.log('Detach response:', response);

      if (response.success) {
        setSuccessMessage(`Successfully detached ${response.detachedCount} player(s)!`);
        // Clear the list after successful detachment
        setUsernames([]);
        setValidationResults(null);
        setExpandedPlayers(new Set());
      } else {
        throw new Error(response.error || 'Failed to detach players');
      }
    } catch (err) {
      console.error('Error detaching players:', err);
      setError(err.message || 'Failed to detach players');
    } finally {
      setIsDetaching(false);
    }
  };

  const handleAutoDetach = async () => {
    if (usernames.length === 0 && totalXUsers === 0) {
      return;
    }

    setIsAutoDetaching(true);
    setError(null);
    setSuccessMessage(null);
    setValidationResults(null);

    // Build the ordered phase list. Players first, then x-types bottom-up
    // (agents → super-agents → cities → countries) so each tier's parent
    // has its children removed before we try to detach it.
    const phases = [];
    if (usernames.length > 0) {
      phases.push({
        key: 'players',
        label: 'players',
        usernames: [...usernames],
        validateEndpoint: '/coreadmin/deep-validate-players',
        detachEndpoint: '/coreadmin/detach-players'
      });
    }
    for (const { key, label } of X_TYPE_ORDER) {
      if (xUsers[key].length > 0) {
        phases.push({
          key,
          label,
          usernames: [...xUsers[key]],
          validateEndpoint: '/coreadmin/deep-validate-users',
          detachEndpoint: '/coreadmin/detach-users'
        });
      }
    }

    const BATCH_SIZE = 300;
    const totalEntities = phases.reduce((sum, p) => sum + p.usernames.length, 0);
    let processedAcrossAllPhases = 0;
    let totalDetached = 0;
    let totalFailed = 0;
    let allValidationResults = [];

    console.log(`Starting auto-detach across ${phases.length} phase(s), ${totalEntities} total entities`);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      for (let phaseIndex = 0; phaseIndex < phases.length; phaseIndex++) {
        const phase = phases[phaseIndex];
        const phaseBatches = Math.ceil(phase.usernames.length / BATCH_SIZE);
        console.log(`Phase ${phaseIndex + 1}/${phases.length} (${phase.label}): ${phase.usernames.length} entities in ${phaseBatches} batch(es)`);

        for (let batchIndex = 0; batchIndex < phaseBatches; batchIndex++) {
          const startIdx = batchIndex * BATCH_SIZE;
          const endIdx = Math.min(startIdx + BATCH_SIZE, phase.usernames.length);
          const batchUsernames = phase.usernames.slice(startIdx, endIdx);

          setAutoDetachProgress({
            totalPlayers: totalEntities,
            processedPlayers: processedAcrossAllPhases,
            detachedCount: totalDetached,
            failedCount: totalFailed,
            currentBatch: batchIndex + 1,
            totalBatches: phaseBatches,
            phaseIndex: phaseIndex + 1,
            totalPhases: phases.length,
            phaseLabel: phase.label,
            status: 'validating'
          });

          // Step 1: Deep validate this batch
          let validateResponse;
          try {
            validateResponse = await apiPost(
              'coreAdmin',
              phase.validateEndpoint,
              { usernames: batchUsernames },
              token
            );
          } catch (validateError) {
            console.error(`[${phase.label}] Batch ${batchIndex + 1} validation request failed:`, validateError);
            totalFailed += batchUsernames.length;
            processedAcrossAllPhases += batchUsernames.length;
            continue;
          }

          if (!validateResponse.success) {
            console.error(`[${phase.label}] Batch ${batchIndex + 1} validation failed:`, validateResponse.error);
            totalFailed += batchUsernames.length;
            processedAcrossAllPhases += batchUsernames.length;
            continue;
          }

          const validation = validateResponse.validation;
          allValidationResults = [...allValidationResults, ...validation.players];

          // Step 2: Detach the valid entities from this batch
          if (validation.validCount > 0) {
            const validInBatch = validation.players
              .filter((p) => p.valid)
              .map((p) => p.username);

            setAutoDetachProgress((prev) => ({
              ...prev,
              status: 'detaching',
              detachingCount: validInBatch.length
            }));

            try {
              const detachResponse = await apiPost(
                'coreAdmin',
                phase.detachEndpoint,
                { usernames: validInBatch },
                token
              );

              if (detachResponse.success) {
                totalDetached += detachResponse.detachedCount || 0;
                totalFailed += detachResponse.failedCount || 0;
                console.log(`[${phase.label}] Batch ${batchIndex + 1}: detached ${detachResponse.detachedCount}, failed ${detachResponse.failedCount || 0}`);
              } else {
                console.error(`[${phase.label}] Batch ${batchIndex + 1} detach failed:`, detachResponse.error);
                totalFailed += validInBatch.length;
              }
            } catch (detachError) {
              console.error(`[${phase.label}] Batch ${batchIndex + 1} detach request failed:`, detachError);
              totalFailed += validInBatch.length;
            }
          } else {
            totalFailed += validation.invalidCount;
          }

          processedAcrossAllPhases += batchUsernames.length;

          if (batchIndex < phaseBatches - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        // Give DynamoDB a moment to propagate parent.children updates before
        // we move up the hierarchy. Validation uses eventually-consistent
        // reads, so a short delay greatly reduces spurious "still has children"
        // failures on the next tier.
        if (phaseIndex < phases.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      // Final update
      setAutoDetachProgress({
        totalPlayers: totalEntities,
        processedPlayers: totalEntities,
        detachedCount: totalDetached,
        failedCount: totalFailed,
        currentBatch: 0,
        totalBatches: 0,
        phaseIndex: phases.length,
        totalPhases: phases.length,
        phaseLabel: 'done',
        status: 'complete'
      });

      setSuccessMessage(
        `Auto-detach complete! Detached ${totalDetached} entit${totalDetached === 1 ? 'y' : 'ies'}, ${totalFailed} failed or invalid (across ${phases.length} phase${phases.length === 1 ? '' : 's'}).`
      );

      setValidationResults({
        totalCount: totalEntities,
        validCount: totalDetached,
        invalidCount: totalFailed,
        players: allValidationResults
      });

      // Clear the input lists - everything has been attempted
      setUsernames([]);
      setXUsers(EMPTY_X_USERS);
      setExpandedXSections(new Set());

    } catch (err) {
      console.error('Error in auto-detach:', err);
      setError(err.message || 'Failed to auto-detach');
    } finally {
      setIsAutoDetaching(false);
      setTimeout(() => setAutoDetachProgress(null), 5000); // Clear progress after 5 seconds
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            <span>Back</span>
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className="relative">
            <div className="h-24 bg-gradient-to-r from-orange-500 to-red-600 dark:from-orange-600 dark:to-red-700" />
            <div className="absolute -bottom-6 left-8">
              <div className="w-16 h-16 rounded-xl bg-white dark:bg-gray-700 shadow-lg flex items-center justify-center">
                <UserMinusIcon className="w-10 h-10 text-orange-500" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 pt-12 pb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Detach Inactive Players
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              Search for and detach inactive player accounts from the system
            </p>

            {/* Input Form */}
            <div className="space-y-6">
              {/* Username Input */}
              <div>
                <label 
                  htmlFor="inputText" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Player Usernames
                </label>
                <textarea
                  id="inputText"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter usernames separated by commas or new lines (e.g., player1, player2, player3 or one per line)..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 focus:border-transparent transition-all duration-200 resize-none"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Tip: Paste a comma-separated list, one per line, or mixed format
                </p>
              </div>

              {/* Add Button */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={handleAddUsernames}
                  disabled={!inputText.trim()}
                  className="flex items-center justify-center space-x-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <UserMinusIcon className="w-5 h-5" />
                  <span>Add Usernames</span>
                </button>

                <button
                  onClick={handleAutoLoad}
                  disabled={isAutoLoading}
                  className="flex items-center justify-center space-x-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {isAutoLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span>Auto-load Players</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Username Pills Display */}
            {usernames.length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Players to Check ({usernames.length})
                    </h3>
                    {usernames.length > 300 && (
                      <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full font-medium">
                        Limited to 300
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                  >
                    Clear All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {usernames.map((username) => (
                    <div
                      key={username}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm"
                    >
                      <span>{username}</span>
                      <button
                        onClick={() => handleRemoveUsername(username)}
                        className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Validate and Auto-detach Buttons */}
                <div className="space-y-3 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={handleValidate}
                      disabled={isValidating || isDeepValidating || isAutoDetaching}
                      className="flex items-center justify-center space-x-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {isValidating ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                          <span>Validating...</span>
                        </>
                      ) : (
                        <>
                          <InformationCircleIcon className="w-5 h-5" />
                          <span>Quick Validate</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleDeepValidate}
                      disabled={isValidating || isDeepValidating || isAutoDetaching}
                      className="flex items-center justify-center space-x-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {isDeepValidating ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                          <span>Deep Checking...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          <span>Deep Validate</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Auto-detach Button - Full Width */}
                  <button
                    onClick={handleAutoDetach}
                    disabled={isValidating || isDeepValidating || isAutoDetaching}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-4 border-2 border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {isAutoDetaching ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        <span>Auto-detaching...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                        <span>
                          Auto-detach All ({usernames.length} player{usernames.length === 1 ? '' : 's'}
                          {totalXUsers > 0 ? ` + ${totalXUsers} hierarchy node${totalXUsers === 1 ? '' : 's'}` : ''})
                        </span>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-center text-gray-500 dark:text-gray-400 -mt-1">
                    Validates and detaches in batches of 300, cascading bottom-up: players → x-agents → x super-agents → x cities → x countries
                  </p>
                </div>
              </div>
            )}

            {/* Detected Inactive Hierarchy (xagents / x super-agents / x cities / x countries) */}
            {totalXUsers > 0 && (
              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    Inactive Hierarchy Detected ({totalXUsers})
                  </h3>
                  <button
                    onClick={() => {
                      setXUsers(EMPTY_X_USERS);
                      setExpandedXSections(new Set());
                    }}
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                  >
                    Clear hierarchy
                  </button>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                  These ancestors became fully inactive once their descendants below were classified as inactive. They will be detached bottom-up after the inactive players above.
                </p>
                <div className="space-y-2">
                  {X_TYPE_ORDER.map(({ key, label }) => {
                    const list = xUsers[key];
                    if (list.length === 0) return null;
                    const isExpanded = expandedXSections.has(key);
                    return (
                      <div
                        key={key}
                        className="bg-white dark:bg-gray-800 rounded-lg border border-amber-300 dark:border-amber-700 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleXSection(key)}
                          className="w-full px-3 py-2 flex items-center justify-between hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">
                            {label} <span className="text-amber-700 dark:text-amber-400 font-semibold">({list.length})</span>
                          </span>
                          {isExpanded ? (
                            <ChevronUpIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-3 pt-1 border-t border-amber-200 dark:border-amber-800">
                            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                              {list.map((username) => (
                                <span
                                  key={username}
                                  className="inline-block px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs rounded-full font-mono"
                                >
                                  {username}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* If only x-users exist (no players in list), still show an Auto-detach button */}
            {usernames.length === 0 && totalXUsers > 0 && (
              <div className="mt-4">
                <button
                  onClick={handleAutoDetach}
                  disabled={isValidating || isDeepValidating || isAutoDetaching}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-4 border-2 border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {isAutoDetaching ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      <span>Auto-detaching hierarchy...</span>
                    </>
                  ) : (
                    <>
                      <UserMinusIcon className="w-5 h-5" />
                      <span>Auto-detach Hierarchy ({totalXUsers} node{totalXUsers === 1 ? '' : 's'})</span>
                    </>
                  )}
                </button>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-1">
                  Cascades bottom-up: x-agents → x super-agents → x cities → x countries
                </p>
              </div>
            )}

            {/* Auto-detach Progress */}
            {autoDetachProgress && (
              <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-600 border-t-transparent flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
                        {autoDetachProgress.status === 'complete'
                          ? '✓ Auto-detach Complete!'
                          : `Auto-detach in progress${autoDetachProgress.totalPhases > 1
                              ? ` — Phase ${autoDetachProgress.phaseIndex}/${autoDetachProgress.totalPhases} (${autoDetachProgress.phaseLabel})`
                              : ''}${autoDetachProgress.totalBatches > 0
                              ? `, batch ${autoDetachProgress.currentBatch}/${autoDetachProgress.totalBatches}`
                              : ''}`}
                      </p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-emerald-700 dark:text-emerald-300">
                          <span>
                            {autoDetachProgress.status === 'validating' && `🔍 Validating ${autoDetachProgress.phaseLabel || ''}...`}
                            {autoDetachProgress.status === 'detaching' && `🔧 Detaching ${autoDetachProgress.detachingCount} valid ${autoDetachProgress.phaseLabel || 'entit'}...`}
                            {autoDetachProgress.status === 'complete' && '✅ All phases processed'}
                          </span>
                          <span className="font-medium">{autoDetachProgress.processedPlayers}/{autoDetachProgress.totalPlayers} entities</span>
                        </div>
                        <div className="w-full bg-emerald-200 dark:bg-emerald-800 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-emerald-600 dark:bg-emerald-400 transition-all duration-500"
                            style={{ width: `${autoDetachProgress.totalPlayers > 0 ? (autoDetachProgress.processedPlayers / autoDetachProgress.totalPlayers) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-emerald-700 dark:text-emerald-300">
                          <span className="font-medium">✓ Detached: {autoDetachProgress.detachedCount}</span>
                          <span className="font-medium">✗ Failed: {autoDetachProgress.failedCount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      <strong>Error:</strong> {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      <strong>Success:</strong> {successMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Validation Results */}
            {validationResults && (
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Validation Results
                </h3>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl border-2 border-green-300 dark:border-green-700 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-bold text-green-700 dark:text-green-400">
                          {validationResults.validCount}
                        </div>
                        <div className="text-sm font-medium text-green-600 dark:text-green-500 mt-1">
                          ✓ Can Detach
                        </div>
                      </div>
                      <CheckCircleIcon className="w-12 h-12 text-green-500 dark:text-green-400 opacity-20" />
                    </div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl border-2 border-red-300 dark:border-red-700 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-bold text-red-700 dark:text-red-400">
                          {validationResults.invalidCount}
                        </div>
                        <div className="text-sm font-medium text-red-600 dark:text-red-500 mt-1">
                          ✗ Cannot Detach
                        </div>
                      </div>
                      <ExclamationTriangleIcon className="w-12 h-12 text-red-500 dark:text-red-400 opacity-20" />
                    </div>
                  </div>
                </div>

                {/* Valid Players Section */}
                {validationResults.validCount > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 flex items-center gap-2">
                        <CheckCircleIcon className="w-5 h-5" />
                        Valid for Detachment ({validationResults.validCount})
                      </h4>
                      <button
                        onClick={() => {
                          const validUsernames = validationResults.players.filter(p => p.valid).map(p => p.username);
                          const allExpanded = validUsernames.every(u => expandedPlayers.has(u));
                          if (allExpanded) {
                            // Collapse all valid players
                            setExpandedPlayers(new Set(Array.from(expandedPlayers).filter(u => 
                              !validUsernames.includes(u)
                            )));
                          } else {
                            // Expand all valid players
                            setExpandedPlayers(new Set([...expandedPlayers, ...validUsernames]));
                          }
                        }}
                        className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium flex items-center gap-1"
                      >
                        {validationResults.players.filter(p => p.valid).every(p => expandedPlayers.has(p.username)) ? (
                          <>
                            <ChevronUpIcon className="w-4 h-4" />
                            Collapse All
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="w-4 h-4" />
                            Expand All
                          </>
                        )}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {validationResults.players.filter(p => p.valid).map((player) => (
                        <div
                          key={player.username}
                          className="bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-300 dark:border-green-700 overflow-hidden shadow-sm"
                        >
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <button
                                onClick={() => togglePlayerExpanded(player.username)}
                                className="flex-1 text-left hover:opacity-80 transition-opacity"
                              >
                                <div className="flex items-center gap-3 mb-1">
                                  <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                                  <p className="font-semibold text-gray-900 dark:text-white text-base">
                                    {player.username}
                                  </p>
                                  {expandedPlayers.has(player.username) ? (
                                    <ChevronUpIcon className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                  ) : (
                                    <ChevronDownIcon className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                  )}
                                </div>
                                <p className="text-sm text-green-700 dark:text-green-400 ml-8">
                                  {player.reason}
                                </p>
                              </button>
                              
                              {/* Individual Detach Button */}
                              <button
                                onClick={() => handleDetachSingle(player.username)}
                                disabled={detachingPlayers.has(player.username)}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center gap-2 flex-shrink-0"
                              >
                                {detachingPlayers.has(player.username) ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                    <span>Detaching...</span>
                                  </>
                                ) : (
                                  <>
                                    <UserMinusIcon className="w-4 h-4" />
                                    <span>Detach</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {expandedPlayers.has(player.username) && player.details && (
                            <div className="px-4 pb-4 bg-white dark:bg-gray-800/50 border-t border-green-300 dark:border-green-700">
                              <div className="mt-3 space-y-2">
                                <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                                  Validation Details
                                </h5>
                                <div className="grid grid-cols-1 gap-2">
                                  {Object.entries(player.details).map(([key, value]) => {
                                    // Format the value for display
                                    let displayValue = value;
                                    if (typeof value === 'object' && value !== null) {
                                      displayValue = JSON.stringify(value, null, 2);
                                    } else if (typeof value === 'boolean') {
                                      displayValue = value ? '✓ Yes' : '✗ No';
                                    }

                                    return (
                                      <div key={key} className="flex justify-between items-start p-2 bg-gray-50 dark:bg-gray-700/30 rounded">
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">
                                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}:
                                        </span>
                                        <span className="text-xs text-gray-900 dark:text-gray-100 text-right ml-2 max-w-xs break-words">
                                          {typeof value === 'object' ? (
                                            <pre className="text-[10px] font-mono">{displayValue}</pre>
                                          ) : (
                                            displayValue
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invalid Players Section */}
                {validationResults.invalidCount > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 flex items-center gap-2">
                        <ExclamationTriangleIcon className="w-5 h-5" />
                        Cannot Detach ({validationResults.invalidCount})
                      </h4>
                      <button
                        onClick={() => {
                          const invalidUsernames = validationResults.players.filter(p => !p.valid).map(p => p.username);
                          const allExpanded = invalidUsernames.every(u => expandedPlayers.has(u));
                          if (allExpanded) {
                            // Collapse all invalid players
                            setExpandedPlayers(new Set(Array.from(expandedPlayers).filter(u => 
                              !invalidUsernames.includes(u)
                            )));
                          } else {
                            // Expand all invalid players
                            setExpandedPlayers(new Set([...expandedPlayers, ...invalidUsernames]));
                          }
                        }}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium flex items-center gap-1"
                      >
                        {validationResults.players.filter(p => !p.valid).every(p => expandedPlayers.has(p.username)) ? (
                          <>
                            <ChevronUpIcon className="w-4 h-4" />
                            Collapse All
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="w-4 h-4" />
                            Expand All
                          </>
                        )}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {validationResults.players.filter(p => !p.valid).map((player) => (
                        <div
                          key={player.username}
                          className="bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-300 dark:border-red-700 overflow-hidden shadow-sm"
                        >
                          <button
                            onClick={() => togglePlayerExpanded(player.username)}
                            className="w-full p-4 text-left hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                                  <p className="font-semibold text-gray-900 dark:text-white text-base">
                                    {player.username}
                                  </p>
                                </div>
                                <p className="text-sm text-red-700 dark:text-red-400 ml-8 font-medium">
                                  {player.reason}
                                </p>
                              </div>
                              {expandedPlayers.has(player.username) ? (
                                <ChevronUpIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                              ) : (
                                <ChevronDownIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                              )}
                            </div>
                          </button>

                          {/* Expanded Details */}
                          {expandedPlayers.has(player.username) && player.details && (
                            <div className="px-4 pb-4 bg-white dark:bg-gray-800/50 border-t border-red-300 dark:border-red-700">
                              <div className="mt-3 space-y-2">
                                <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
                                  Validation Details
                                </h5>
                                <div className="grid grid-cols-1 gap-2">
                                  {Object.entries(player.details).map(([key, value]) => {
                                    // Format the value for display
                                    let displayValue = value;
                                    let valueColor = 'text-gray-900 dark:text-gray-100';
                                    
                                    if (typeof value === 'object' && value !== null) {
                                      displayValue = JSON.stringify(value, null, 2);
                                    } else if (typeof value === 'boolean') {
                                      displayValue = value ? '✓ Yes' : '✗ No';
                                      valueColor = value ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                                    } else if (typeof value === 'number' && key.toLowerCase().includes('days')) {
                                      // Highlight days in red if < 45
                                      valueColor = value < 45 ? 'text-red-600 dark:text-red-400 font-bold' : 'text-green-600 dark:text-green-400';
                                    }

                                    return (
                                      <div key={key} className="flex justify-between items-start p-2 bg-gray-50 dark:bg-gray-700/30 rounded">
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">
                                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}:
                                        </span>
                                        <span className={`text-xs ${valueColor} text-right ml-2 max-w-xs break-words`}>
                                          {typeof value === 'object' ? (
                                            <pre className="text-[10px] font-mono">{displayValue}</pre>
                                          ) : (
                                            displayValue
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detach All Button */}
                {validationResults.validCount > 0 && (
                  <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-700">
                    <button
                      onClick={handleDetachAll}
                      disabled={isDetaching}
                      className="w-full flex items-center justify-center space-x-2 px-6 py-4 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {isDetaching ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                          <span>Detaching All...</span>
                        </>
                      ) : (
                        <>
                          <UserMinusIcon className="w-5 h-5" />
                          <span>Detach All {validationResults.validCount} Valid Player{validationResults.validCount !== 1 ? 's' : ''}</span>
                        </>
                      )}
                    </button>
                    <p className="mt-2 text-xs text-center text-gray-500 dark:text-gray-400">
                      ⚠️ This will permanently detach all valid players from the user tree
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>How it works:</strong>
                  </p>
                  <ol className="text-sm text-blue-800 dark:text-blue-200 list-decimal list-inside mt-2 space-y-1">
                    <li><strong>Manual:</strong> Enter player usernames separated by commas or new lines, then click "Add Usernames"</li>
                    <li><strong>Auto-load:</strong> Click "Auto-load Players" to find inactive players AND detect ancestors (x-agents, x super-agents, x cities, x countries) whose entire subtree is inactive</li>
                    <li><strong>Quick Validate:</strong> Fast check using cached user tree data (players only)</li>
                    <li><strong>Deep Validate:</strong> Thorough backend check for players (GameTransactions, wallets, all currency variants)</li>
                    <li><strong>Auto-detach:</strong> Validates + detaches in batches of 300, cascading bottom-up: players → x-agents → x super-agents → x cities → x countries</li>
                    <li><strong>Manual:</strong> Review validation results and click individual "Detach" or "Detach All"</li>
                  </ol>
                  <div className="mt-3 pt-3 border-t border-blue-300 dark:border-blue-700">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Deep Validate checks:</strong>
                    </p>
                    <ul className="text-xs text-blue-700 dark:text-blue-300 list-disc list-inside mt-1 space-y-0.5">
                      <li>Last bet in GameTransactions table (must be &gt; 45 days ago)</li>
                      <li>Actual wallet balances from backend: USD &lt; 10, LBP &lt; 1,000,000, TND &lt; 10, TRY &lt; 15, IQD &lt; 15,000, KWD &lt; 3</li>
                      <li>Last transaction for username and all currency variants (username-usd, username-lbp, username-tnd, username-try, username-kwd)</li>
                      <li>All activity must be &gt; 45 days old to be marked as truly inactive</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning Box */}
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Warning:</strong> Detaching players is a sensitive operation that affects the user tree structure.
                  </p>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-1">
                    The validation process will check (applies to players and to x-agents / x super-agents / x cities / x countries):
                  </p>
                  <ul className="text-sm text-yellow-800 dark:text-yellow-200 list-disc list-inside mt-1 space-y-0.5">
                    <li>User exists in the system and type is detachable (player, agent, super-agent, city, country)</li>
                    <li>User has no remaining children at detach time (cascade ensures this bottom-up)</li>
                    <li>User has no recent activity (45+ days)</li>
                    <li>User wallets below thresholds (USD &lt; 10, LBP &lt; 1M, TND &lt; 10, TRY &lt; 15, IQD &lt; 15,000, KWD &lt; 3)</li>
                  </ul>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-2">
                    On detach, the type is rewritten with an <code className="font-mono">x</code> prefix (e.g. <code className="font-mono">agent → xagent</code>, <code className="font-mono">super-agent → xsuper-agent</code>).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetachInactivePlayers;

